import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash-es';
import ms from 'ms';
import type { GetStaticPropsResult } from 'next';
import Router from 'next/router';
import { faXmark } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faMagnifyingGlass } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import type { ExecutionResultWithData } from '@soundxyz/graphql-react-query';
import { MINIMIZED_PLAYER_HEIGHT } from '@components/AudioPlayerMobile/MinimizedPlayer';
import { ExplorePage } from '@components/Explore/ExplorePage';
import { SEARCH_TAB_CHOICES } from '@components/Explore/schema';
import { setActiveTab } from '@components/Explore/useSearchPageStore';
import { Seo } from '@components/Seo';
import { ClearSearchButton, SearchInput } from '@components/main-components/search/SearchInput';
import { ParamsStore } from '@context/params';
import type { InitialSearchResultsQuery } from '@graphql/reactQuery';
import { fetchGQL, InitialSearchResultsDocument } from '@graphql/reactQuery';
import { globalSearchSoundState, useSearchInputNew } from '@hooks/search/useSearchInputNew';

interface PageProps {
  initialData: ExecutionResultWithData<InitialSearchResultsQuery>;
}

export async function getStaticProps(): Promise<GetStaticPropsResult<PageProps>> {
  const initialData = await fetchGQL(InitialSearchResultsDocument, { variables: {} });

  return {
    props: {
      initialData,
    },
    revalidate: ms('5 minutes'),
  };
}

export default function Explore() {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { barInputText, handleInputChange, clearText } = useSearchInputNew({
    searchState: globalSearchSoundState,
  });

  const onEnterCloseVirtualKeyboard = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  const goToSearch = useRef(
    debounce((val: string) => {
      Router.push({
        pathname: '/search',
        query: { q: val },
      }).then(() => {
        ParamsStore.tab = SEARCH_TAB_CHOICES.ALL;
        setActiveTab({ activeTab: SEARCH_TAB_CHOICES.ALL });
      });
    }, 500),
  ).current;

  return (
    <>
      <Seo title="Explore | Sound.xyz" />

      <div className={`w-full bg-white pb-12 md:pb-16 md:pb-[${MINIMIZED_PLAYER_HEIGHT}px]`}>
        <div className="flex w-full px-4 pb-5 md:hidden">
          <div className="flex h-10 w-[99%] items-center rounded-md border border-solid border-neutral300 bg-white focus-within:bg-white">
            <FontAwesomeIcon className="ml-3 stroke-neutral800" icon={faMagnifyingGlass} />
            <SearchInput
              ref={searchInputRef}
              type="text"
              placeholder="Search for anything on Sound"
              value={barInputText}
              onChange={e => {
                goToSearch.cancel();

                handleInputChange(e);
                ParamsStore.tab = SEARCH_TAB_CHOICES.ALL;
                setActiveTab({ activeTab: SEARCH_TAB_CHOICES.ALL });

                goToSearch(e.target.value);
              }}
              onKeyUp={onEnterCloseVirtualKeyboard}
            />
            {!!barInputText && (
              <ClearSearchButton
                className="mr-3"
                onClick={() => clearText()}
                aria-label="Clear search input"
              >
                <FontAwesomeIcon icon={faXmark} size="lg" />
              </ClearSearchButton>
            )}
          </div>
        </div>
        <ExplorePage />
      </div>
    </>
  );
}
